import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { propertiesApi, propertiesTypeApi, tagsApi, countryApi, sectionApi, addressesApi } from '../../services/adminApi';
import '../../styles/AdminPages.css';

const API_URL = 'https://api.tannit.uk';

const energyLabelColors = {
  'A': '#00a651',
  'B': '#50b848',
  'C': '#bdd62e',
  'D': '#fff200',
  'E': '#fcb813',
  'F': '#f37021',
  'G': '#ed1c24'
};

const EditPropertyPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState('');
  const [error, setError] = useState('');
  
  const fileInputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [originalProperty, setOriginalProperty] = useState(null);

  const [formData, setFormData] = useState({
    propertyName: '',
    price: '',
    description: '',
    propertyTypeId: '',
    archived: false,
    addressId: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    countryId: '',
    latitude: '',
    longitude: '',
    tagValues: {},
    images: []
  });

  const [tagSections, setTagSections] = useState([]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (mapLoaded && currentStep === 2 && mapRef.current && window.L) {
      const lat = formData.latitude || 51.5074;
      const lng = formData.longitude || -0.1278;
      
      if (!mapRef.current._leaflet_id) {
        const map = window.L.map(mapRef.current).setView([lat, lng], 13);
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);
        
        const marker = window.L.marker([lat, lng], { draggable: true }).addTo(map);
        markerRef.current = marker;
        
        marker.on('dragend', function(e) {
          const pos = e.target.getLatLng();
          setFormData(prev => ({
            ...prev,
            latitude: pos.lat.toFixed(6),
            longitude: pos.lng.toFixed(6)
          }));
        });
        
        map.on('click', function(e) {
          marker.setLatLng(e.latlng);
          setFormData(prev => ({
            ...prev,
            latitude: e.latlng.lat.toFixed(6),
            longitude: e.latlng.lng.toFixed(6)
          }));
        });
        
        mapRef.current._leaflet_map = map;
      }
    }
  }, [mapLoaded, currentStep, formData.latitude, formData.longitude]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      setDataError('');
      try {
        const [propertyRes, typesRes, tagsRes, countriesRes, sectionsRes] = await Promise.all([
          axios.get(`${API_URL}/Property/GetPropertyWithTags/${propertyId}`),
          propertiesTypeApi.getAll(),
          tagsApi.getAll(),
          countryApi.getAll(),
          sectionApi.getAll()
        ]);
        
        const propertyData = propertyRes?.data?.data?.[0] || propertyRes?.data?.[0] || propertyRes?.data;
        const property = Array.isArray(propertyData) ? propertyData[0] : propertyData;
        setOriginalProperty(property);
        
        const types = typesRes?.data?.[0] || typesRes?.data || [];
        const tags = tagsRes?.data?.[0] || tagsRes?.data || [];
        const countryList = countriesRes?.data?.[0] || countriesRes?.data || [];
        const sections = sectionsRes?.data?.[0] || sectionsRes?.data || [];
        
        setPropertyTypes(Array.isArray(types) ? types : []);
        setCountries(Array.isArray(countryList) ? countryList : []);
        
        const sectionMap = {};
        if (Array.isArray(sections)) {
          sections.forEach(s => {
            sectionMap[s.id] = { id: s.id, name: s.sectionName, tags: [] };
          });
        }
        
        if (Array.isArray(tags)) {
          tags.forEach(tag => {
            const sectionId = tag.sectionId;
            if (sectionMap[sectionId]) {
              sectionMap[sectionId].tags.push({
                tagId: tag.tagId,
                label: tag.label,
                fontAwesomeId: tag.fontAwesomeId,
                unit: tag.unit || null
              });
            }
          });
        }
        
        const sectionsArray = Object.values(sectionMap).filter(s => s.tags.length > 0);
        setTagSections(sectionsArray);
        
        const address = property.address || {};
        const propertyTags = property.tags || [];
        
        const tagValues = {};
        propertyTags.forEach(pt => {
          if (pt.boolValue !== null && pt.boolValue !== undefined) {
            tagValues[pt.tagId] = { value: pt.boolValue, unit: pt.unit || 'bool' };
          } else if (pt.decimalValue !== null && pt.decimalValue !== undefined) {
            tagValues[pt.tagId] = { value: pt.decimalValue, unit: pt.unit };
          }
        });
        
        const existingImages = (property.images || []).map((img, idx) => ({
          imageId: img.imageId,
          url: img.url,
          filename: img.url?.split('/').pop() || `image-${idx}`,
          isMain: img.isMain || idx === 0
        }));
        
        setFormData({
          propertyName: property.propertyName || '',
          price: property.price || '',
          description: property.description || property.propertyDescription || '',
          propertyTypeId: property.propertyTypeId || '',
          archived: property.archived || false,
          addressId: property.addressId || address.addressId || '',
          street: address.streetAddress || '',
          city: address.city || '',
          state: address.state || '',
          postalCode: address.postalCode || '',
          countryId: address.countryId || '',
          latitude: address.latitude || '',
          longitude: address.longitude || '',
          tagValues: tagValues,
          images: existingImages
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        setDataError('Failed to load property data. Please try again.');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [propertyId]);

  const searchAddress = async () => {
    const query = `${formData.street}, ${formData.city}, ${formData.postalCode}`.trim();
    if (!query || query === ', ,') return;
    
    setSearchingAddress(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`);
      const results = await response.json();
      
      if (results && results.length > 0) {
        const { lat, lon } = results[0];
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(lat).toFixed(6),
          longitude: parseFloat(lon).toFixed(6)
        }));
        
        if (mapRef.current?._leaflet_map && markerRef.current) {
          const newLatLng = [parseFloat(lat), parseFloat(lon)];
          mapRef.current._leaflet_map.setView(newLatLng, 15);
          markerRef.current.setLatLng(newLatLng);
        }
      } else {
        Swal.fire({
          title: 'Address Not Found',
          text: 'Could not find coordinates for this address. Please adjust manually on the map.',
          icon: 'info'
        });
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    } finally {
      setSearchingAddress(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTagValueChange = (tagId, value, unit) => {
    setFormData(prev => ({
      ...prev,
      tagValues: {
        ...prev.tagValues,
        [tagId]: { value, unit }
      }
    }));
  };

  const handleGroupSelection = (sectionId, tagId, groupType = 'group-1') => {
    setFormData(prev => {
      const section = tagSections.find(s => s.id === sectionId);
      if (!section) return prev;
      
      const groupTags = section.tags.filter(t => t.unit === groupType);
      const newTagValues = { ...prev.tagValues };
      
      groupTags.forEach(t => {
        if (t.tagId === tagId) {
          newTagValues[t.tagId] = { value: true, unit: groupType };
        } else {
          delete newTagValues[t.tagId];
        }
      });
      
      return { ...prev, tagValues: newTagValues };
    });
  };

  const handleBoolChange = (tagId, value) => {
    setFormData(prev => ({
      ...prev,
      tagValues: {
        ...prev.tagValues,
        [tagId]: { value, unit: 'bool' }
      }
    }));
  };

  const getInputType = (unit) => {
    if (unit === 'bool') return 'bool';
    if (unit === 'group-1') return 'group-1';
    if (unit === 'group-2') return 'group-2';
    if (!unit) return 'checkbox';
    return 'number';
  };

  const isEnergyLabel = (label) => {
    return ['A', 'B', 'C', 'D', 'E', 'F', 'G'].includes(label?.trim());
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    const validFiles = Array.from(files).filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 10 * 1024 * 1024;
      return isImage && isValidSize;
    });

    if (validFiles.length === 0) {
      Swal.fire({
        title: 'Invalid Files',
        text: 'Please upload valid image files (max 10MB each)',
        icon: 'warning'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadedImages = [];
      
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const formDataUpload = new FormData();
        formDataUpload.append('image', file);
        
        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formDataUpload
        });
        
        if (response.ok) {
          const result = await response.json();
          uploadedImages.push({
            url: result.url,
            filename: result.filename,
            isMain: formData.images.length === 0 && i === 0
          });
        }
        
        setUploadProgress(Math.round(((i + 1) / validFiles.length) * 100));
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
      
    } catch (err) {
      console.error('Upload error:', err);
      Swal.fire({
        title: 'Upload Failed',
        text: 'Failed to upload images. Please try again.',
        icon: 'error'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSetMainImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({ ...img, isMain: i === index }))
    }));
  };

  const validateStep = (step) => {
    const errors = [];
    
    switch (step) {
      case 1:
        if (!formData.propertyName.trim()) errors.push('Property name is required');
        if (!formData.price || formData.price <= 0) errors.push('Valid price is required');
        if (!formData.propertyTypeId) errors.push('Property type is required');
        break;
      case 2:
        if (!formData.street.trim()) errors.push('Street address is required');
        if (!formData.city.trim()) errors.push('City is required');
        if (!formData.countryId) errors.push('Country is required');
        break;
      default:
        break;
    }
    
    return errors;
  };

  const nextStep = () => {
    const errors = validateStep(currentStep);
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }
    setError('');
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      if (formData.addressId) {
        const addressData = {
          addressId: Number(formData.addressId),
          streetAddress: formData.street,
          streetAddress2: originalProperty?.address?.streetAddress2 || '',
          streetNumber: originalProperty?.address?.streetNumber || 0,
          buildingNumber: originalProperty?.address?.buildingNumber || '0',
          appartNumber: originalProperty?.address?.appartNumber || 0,
          city: formData.city,
          state: formData.state || '',
          countryId: Number(formData.countryId),
          postalCode: formData.postalCode || '',
          latitude: formData.latitude ? Number(formData.latitude) : 0,
          longitude: formData.longitude ? Number(formData.longitude) : 0,
          isActive: true
        };
        await addressesApi.update(formData.addressId, addressData);
      }

      const propertyData = {
        propertyId: Number(propertyId),
        propertyName: formData.propertyName,
        price: Number(formData.price),
        description: formData.description || '',
        propertyTypeId: Number(formData.propertyTypeId),
        addressId: Number(formData.addressId),
        archived: formData.archived
      };

      await propertiesApi.update(propertyId, propertyData);
      
      Swal.fire({
        title: 'Success!',
        text: 'Property has been updated successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      navigate(`/admin/properties/view/${propertyId}`);
    } catch (err) {
      console.error('Error updating property:', err);
      const errorStatus = err.status ? `[${err.status}] ` : '';
      let errorMessage = err.message || 'Failed to update property';
      
      if (err.originalError?.response?.data?.Status?.Message) {
        errorMessage = err.originalError.response.data.Status.Message;
      }
      
      setError(`${errorStatus}${errorMessage}`);
      
      Swal.fire({
        title: 'Error',
        html: `<div style="text-align:left;font-size:14px;"><strong>Failed to update property:</strong><br/>${errorMessage}</div>`,
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Basic Info', icon: '🏠' },
    { number: 2, title: 'Address', icon: '📍' },
    { number: 3, title: 'Tags', icon: '🏷️' },
    { number: 4, title: 'Images', icon: '📷' }
  ];

  if (loadingData) {
    return (
      <div className="admin-page">
        <div className="loading-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="admin-spinner"></div>
          <p>Loading property data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page add-property-page">
      <div className="admin-header">
        <div>
          <h1>Edit Property</h1>
          <p className="admin-subtitle">Update property details for: {originalProperty?.propertyName}</p>
        </div>
        <button className="admin-btn secondary" onClick={() => navigate(`/admin/properties/view/${propertyId}`)}>
          Cancel
        </button>
      </div>

      <div className="step-progress">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div 
              className={`step-item ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
              onClick={() => setCurrentStep(step.number)}
              style={{ cursor: 'pointer' }}
            >
              <div className="step-circle">
                {currentStep > step.number ? '✓' : step.icon}
              </div>
              <span className="step-title">{step.title}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`step-line ${currentStep > step.number ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {dataError && (
        <div className="admin-error" style={{ marginBottom: '20px' }}>
          {dataError}
        </div>
      )}

      {error && (
        <div className="admin-error" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div className="form-section">
        {currentStep === 1 && (
          <div className="step-content">
            <h2>Basic Information</h2>
            <p className="section-subtitle">Update the main details about the property</p>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Property Name *</label>
                <input
                  type="text"
                  value={formData.propertyName}
                  onChange={(e) => handleInputChange('propertyName', e.target.value)}
                  placeholder="Enter property name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Price (£) *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="Enter price"
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Property Type *</label>
                <select
                  value={formData.propertyTypeId}
                  onChange={(e) => handleInputChange('propertyTypeId', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select type...</option>
                  {propertyTypes.map(pt => (
                    <option key={pt.propertyTypeId} value={pt.propertyTypeId}>
                      {pt.propertyTypeName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Archived</label>
                <div className="bool-toggle" style={{ marginTop: '8px' }}>
                  <button
                    type="button"
                    className={`toggle-btn ${formData.archived === false ? 'active yes' : ''}`}
                    onClick={() => handleInputChange('archived', false)}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${formData.archived === true ? 'active no' : ''}`}
                    onClick={() => handleInputChange('archived', true)}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter property description..."
                className="form-input"
                rows={5}
              />
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="step-content">
            <h2>Property Address</h2>
            <p className="section-subtitle">Update the location details for this property</p>
            
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Street Address *</label>
                <input
                  type="text"
                  value={formData.street}
                  onChange={(e) => handleInputChange('street', e.target.value)}
                  placeholder="Enter street address"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter city"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>State / Province</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="Enter state"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Postal Code</label>
                <input
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  placeholder="Enter postal code"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Country *</label>
                <select
                  value={formData.countryId}
                  onChange={(e) => handleInputChange('countryId', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select country...</option>
                  {countries.map(c => (
                    <option key={c.id || c.countryId} value={c.id || c.countryId}>
                      {c.name || c.countryName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <button 
                  type="button" 
                  className="admin-btn secondary"
                  onClick={searchAddress}
                  disabled={searchingAddress}
                  style={{ marginTop: '24px' }}
                >
                  {searchingAddress ? 'Searching...' : 'Find on Map'}
                </button>
              </div>
            </div>

            <div className="form-group full-width" style={{ marginTop: '20px' }}>
              <label>Location on Map (click or drag marker to set position)</label>
              <div 
                ref={mapRef} 
                style={{ 
                  height: '300px', 
                  width: '100%', 
                  borderRadius: '8px', 
                  border: '1px solid #ddd',
                  marginTop: '8px'
                }}
              >
                {!mapLoaded && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f5f5f5' }}>
                    Loading map...
                  </div>
                )}
              </div>
            </div>

            <div className="form-grid" style={{ marginTop: '15px' }}>
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => handleInputChange('latitude', e.target.value)}
                  placeholder="e.g., 51.5074"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', e.target.value)}
                  placeholder="e.g., -0.1278"
                  className="form-input"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="step-content">
            <h2>Property Tags</h2>
            <p className="section-subtitle">Update property attributes grouped by section</p>
            
            {tagSections.length === 0 ? (
              <p className="no-data">No tags available</p>
            ) : (
              <div className="tag-sections">
                {tagSections.map(section => {
                  const group1Tags = section.tags.filter(t => t.unit === 'group-1');
                  const group2Tags = section.tags.filter(t => t.unit === 'group-2');
                  const groupBREAMTags = section.tags.filter(t => t.unit === 'group-BREAM');
                  const otherTags = section.tags.filter(t => t.unit !== 'group-1' && t.unit !== 'group-2' && t.unit !== 'group-BREAM' && !t.label?.toUpperCase().includes('BREAM'));
                  
                  const isEnvironmentalSection = section.name?.toLowerCase().includes('environmental') || 
                                                   group1Tags.some(t => isEnergyLabel(t.label));
                  
                  return (
                    <div key={section.id} className="tag-section">
                      <h3 className="section-header">{section.name}</h3>
                      <div className="section-tags">
                        {group1Tags.length > 0 && (
                          <div className="tag-group-container">
                            <div className="tag-group-label">
                              {isEnvironmentalSection ? 'Energy Label' : 'Select one:'}
                            </div>
                            <div className="tag-radio-group" style={isEnvironmentalSection ? { display: 'flex', gap: '8px', flexWrap: 'wrap' } : {}}>
                              {group1Tags.map(tag => {
                                const isEnergy = isEnergyLabel(tag.label);
                                const energyColor = isEnergy ? energyLabelColors[tag.label.trim()] : null;
                                
                                const isSelected = formData.tagValues[tag.tagId]?.value === true;
                                return (
                                  <label 
                                    key={tag.tagId} 
                                    className={`radio-option ${isEnergy ? 'energy-label-option' : ''}`}
                                    style={isEnergy ? {
                                      backgroundColor: isSelected ? energyColor : '#f5f5f5',
                                      color: isSelected ? '#fff' : '#333',
                                      border: `2px solid ${energyColor}`,
                                      padding: '8px 16px',
                                      borderRadius: '6px',
                                      fontWeight: isSelected ? '700' : '500',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    } : {}}
                                  >
                                    <input
                                      type="radio"
                                      name={`group-1-${section.id}`}
                                      checked={formData.tagValues[tag.tagId]?.value === true}
                                      onChange={() => handleGroupSelection(section.id, tag.tagId, 'group-1')}
                                      style={isEnergy ? { display: 'none' } : {}}
                                    />
                                    <span className="radio-label" style={isEnergy ? { margin: 0, fontWeight: isSelected ? '700' : '500' } : {}}>
                                      {!isEnergy && tag.fontAwesomeId && <i className={tag.fontAwesomeId} />}
                                      {tag.label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {group2Tags.length > 0 && (
                          <div className="tag-group-container" style={{ marginTop: group1Tags.length > 0 ? '20px' : '0' }}>
                            <div className="tag-group-label">Credit Options</div>
                            <div className="tag-radio-group">
                              {group2Tags.map(tag => (
                                <label key={tag.tagId} className="radio-option">
                                  <input
                                    type="radio"
                                    name={`group-2-${section.id}`}
                                    checked={formData.tagValues[tag.tagId]?.value === true}
                                    onChange={() => handleGroupSelection(section.id, tag.tagId, 'group-2')}
                                  />
                                  <span className="radio-label">
                                    {tag.fontAwesomeId && <i className={tag.fontAwesomeId} />}
                                    {tag.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {groupBREAMTags.length > 0 && isEnvironmentalSection && (
                          <div className="tag-group-container" style={{ marginTop: (group1Tags.length > 0 || group2Tags.length > 0) ? '20px' : '0' }}>
                            <div className="tag-group-label">BREEAM</div>
                            <select
                              className="form-input"
                              style={{ maxWidth: '300px' }}
                              value={groupBREAMTags.find(t => formData.tagValues[t.tagId]?.value === true)?.tagId || ''}
                              onChange={(e) => {
                                const selectedTagId = e.target.value;
                                if (selectedTagId) {
                                  handleGroupSelection(section.id, Number(selectedTagId), 'group-BREAM');
                                }
                              }}
                            >
                              <option value="">Select BREEAM rating...</option>
                              {groupBREAMTags.map(tag => (
                                <option key={tag.tagId} value={tag.tagId}>
                                  {tag.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {otherTags.map(tag => {
                          const inputType = getInputType(tag.unit);
                          const isNumberLot = tag.label?.toLowerCase().includes('number lot') || 
                                              tag.label?.toLowerCase().includes('lot number');
                          
                          return (
                            <div key={tag.tagId} className="tag-input-row">
                              <div className="tag-label">
                                {tag.fontAwesomeId && <i className={tag.fontAwesomeId} />}
                                <span>{tag.label}</span>
                                {tag.unit && inputType === 'number' && (
                                  <span className="tag-unit">({tag.unit})</span>
                                )}
                              </div>
                              <div className="tag-input">
                                {inputType === 'bool' && (() => {
                                  const tagValue = formData.tagValues[tag.tagId]?.value;
                                  const isYes = tagValue === true;
                                  const isNo = tagValue === false || tagValue === undefined || tagValue === null;
                                  return (
                                    <div className="bool-toggle">
                                      <button
                                        type="button"
                                        className={`toggle-btn ${isYes ? 'active yes' : ''}`}
                                        onClick={() => handleBoolChange(tag.tagId, true)}
                                      >
                                        Yes
                                      </button>
                                      <button
                                        type="button"
                                        className={`toggle-btn ${isNo ? 'active no' : ''}`}
                                        onClick={() => handleBoolChange(tag.tagId, false)}
                                      >
                                        No
                                      </button>
                                    </div>
                                  );
                                })()}
                                
                                {inputType === 'checkbox' && !isNumberLot && (
                                  <label className="checkbox-option">
                                    <input
                                      type="checkbox"
                                      checked={formData.tagValues[tag.tagId]?.value === true}
                                      onChange={(e) => handleTagValueChange(tag.tagId, e.target.checked, null)}
                                    />
                                    <span className="checkmark"></span>
                                  </label>
                                )}
                                
                                {(inputType === 'number' || isNumberLot) && (
                                  <input
                                    type="number"
                                    step={isNumberLot ? "1" : "0.01"}
                                    value={formData.tagValues[tag.tagId]?.value || ''}
                                    onChange={(e) => handleTagValueChange(tag.tagId, e.target.value, tag.unit || 'number')}
                                    placeholder={isNumberLot ? "Enter lot number" : "Enter value"}
                                    className="form-input"
                                    style={{ maxWidth: '200px' }}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {currentStep === 4 && (
          <div className="step-content">
            <h2>Property Images</h2>
            <p className="section-subtitle">Manage images for this property</p>
            
            <div 
              className={`image-dropzone ${dragActive ? 'active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div className="dropzone-content">
                <div className="dropzone-icon">📷</div>
                <p className="dropzone-title">Drag & drop images here</p>
                <p className="dropzone-subtitle">or click to browse</p>
                <span className="dropzone-formats">Supports: JPG, PNG, WEBP (max 10MB each)</span>
              </div>
            </div>

            {uploading && (
              <div className="upload-progress">
                <div className="upload-progress-bar">
                  <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <span>Uploading... {uploadProgress}%</span>
              </div>
            )}

            <div className="images-grid">
              {formData.images.length === 0 ? (
                <p className="no-data">No images added yet. Drag and drop or click above to upload.</p>
              ) : (
                formData.images.map((img, index) => (
                  <div key={img.imageId || index} className={`image-card ${img.isMain ? 'main-image' : ''}`}>
                    <img src={img.url} alt={`Property ${index + 1}`} onError={(e) => e.target.src = 'https://via.placeholder.com/200x150?text=Error'} />
                    <div className="image-actions-overlay">
                      <button
                        type="button"
                        className={`btn-main ${img.isMain ? 'active' : ''}`}
                        onClick={() => handleSetMainImage(index)}
                        title="Set as main image"
                      >
                        ★
                      </button>
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => handleImageRemove(index)}
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                    {img.isMain && <span className="main-badge">Main</span>}
                  </div>
                ))
              )}
            </div>

            <div className="review-section">
              <h3>Review Property Details</h3>
              <div className="review-grid">
                <div className="review-item">
                  <strong>Name:</strong> {formData.propertyName}
                </div>
                <div className="review-item">
                  <strong>Price:</strong> £{Number(formData.price).toLocaleString()}
                </div>
                <div className="review-item">
                  <strong>Type:</strong> {propertyTypes.find(pt => pt.propertyTypeId === Number(formData.propertyTypeId))?.propertyTypeName || '-'}
                </div>
                <div className="review-item">
                  <strong>Address:</strong> {formData.street}, {formData.city}
                </div>
                <div className="review-item">
                  <strong>Tags:</strong> {Object.keys(formData.tagValues).filter(k => formData.tagValues[k]?.value).length} with values
                </div>
                <div className="review-item">
                  <strong>Images:</strong> {formData.images.length} added
                </div>
                <div className="review-item">
                  <strong>Status:</strong> {formData.archived ? 'Archived' : 'Active'}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="form-navigation">
          {currentStep > 1 && (
            <button type="button" className="admin-btn secondary" onClick={prevStep}>
              Previous
            </button>
          )}
          
          <div className="nav-spacer" />
          
          {currentStep < 4 ? (
            <button type="button" className="admin-btn primary" onClick={nextStep}>
              Next
            </button>
          ) : (
            <button
              type="button"
              className="admin-btn primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditPropertyPage;
